import { Link } from "gatsby"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { FocusOn } from "react-focus-on"
import { useTranslation } from "react-i18next"

import Hamburger from "@atoms/Hamburger/Hamburger"
import IconGridLink from "@atoms/IconGridLink/IconGridLink"
import { MotionBox } from "@atoms/MotionBox/MotionBox"
import logo from "@images/svg/logo-alma-insights.svg"
import iconSearch from "@images/svg/search.svg"
import iconShop from "@images/svg/shopping-cart.svg"
import iconUserActive from "@images/svg/user-active.svg"
import iconUser from "@images/svg/user.svg"
import "@lang/i18n"
import IconGrid from "@molecules/IconGrid/IconGrid"
import Menu from "@organisms/Menu/Menu"
import Search from "@organisms/Search/Search"

import styles from "./Header.module.scss"

const Header = ({
  siteTitle,
  menuItems,
  siteSettings,
  currentLanguage,
  open,
  setOpen,
  customClass,
}) => {
  const { t } = useTranslation()

  const [loginOpen, setLoginOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [delayLeave, setDelayLeave] = useState(false)

  const languageRoot = "/"

  useEffect(() => {
    let handler = null
    if (searchOpen || loginOpen) {
      setDelayLeave(true)
      setOpen(false)
    } else {
      handler = setTimeout(() => setDelayLeave(false), 1000)
    }
    return () => {
      clearTimeout(handler)
    }
  }, [searchOpen, loginOpen, setOpen])

  useEffect(() => {
    if (open) {
      setSearchOpen(false)
      setLoginOpen(false)
    }
  }, [open, setSearchOpen])

  const shopLinkContent = (
    <>
      <img
        className={styles.extrasIcon}
        src={iconShop}
        alt=""
        aria-hidden="true"
      />
      <span className="screen-reader-text">{siteSettings.linkShop?.title}</span>
    </>
  )

  const iconGrid = siteSettings?.loginLinks
    ? siteSettings.loginLinks.map((item, index) => {
        const { target, title, url } = item.link || {}
        const { icon } = item || {}
        return (
          <IconGridLink
            key={index}
            icon={icon?.sourceUrl.replace("f_auto,q_auto/", "")}
            href={url}
            text={title}
            target={target}
          />
        )
      })
    : []

  return (
    <header
      className={`${styles.header} ${customClass} ${
        open ? styles.positionSticky : styles.positionSticky
      }`}
      id="header"
    >
      <div className={styles.container}>
        <Link className={styles.brand} to={languageRoot} aria-hidden={open}>
          <img src={logo} width="300" height="80" alt={siteTitle} />
        </Link>

        <div className={styles.trigger}>
          <Hamburger
            open={open}
            setOpen={setOpen}
            fakeActive={!open && searchOpen}
            setFake={setSearchOpen}
          />

          {/* SEARCH BUTTON FOR MOBILE */}
          <button
            className={styles.mobileSearch}
            aria-hidden={open}
            onClick={() => setSearchOpen(true)}
          >
            <img
              src={iconSearch}
              className={styles.mobileSearch}
              alt=""
              aria-hidden="true"
            />
            <span className="screen-reader-text">{t("Search")}</span>
          </button>

          <div
            className={`${styles.panel} ${
              searchOpen || delayLeave ? styles.stateActive : ""
            }`}
            aria-expanded={searchOpen}
          >
            {searchOpen && (
              <FocusOn
                onEscapeKey={() => setSearchOpen(false)}
                scrollLock={false}
              >
                <div className={`container ${styles.panelContainer}`}>
                  <MotionBox
                    animate={
                      searchOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }
                    }
                    transition={{ delay: 0.2 }}
                    className={styles.content}
                  >
                    <Search currentLanguage={currentLanguage} />

                    <div className="max-vw">
                      <button
                        className={styles.panelClose}
                        onClick={() => setSearchOpen(false)}
                      >
                        <span>{t("Close")}</span>
                      </button>
                    </div>
                  </MotionBox>
                </div>
              </FocusOn>
            )}
          </div>
        </div>

        <ul className={styles.extras}>
          {siteSettings.linkShop && (
            <li>
              {siteSettings.linkShop.target === "_blank" && (
                <a
                  href={siteSettings.linkShop.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {shopLinkContent}
                </a>
              )}
              {siteSettings.linkShop.target !== "_blank" && (
                <a href={siteSettings.linkShop.url}>{shopLinkContent}</a>
              )}
            </li>
          )}
          <li className={styles.loginTriggerContainer}>
            <button
              className={styles.loginTrigger}
              onClick={() => setLoginOpen(!loginOpen)}
            >
              <img
                src={loginOpen ? iconUserActive : iconUser}
                className={styles.extrasIcon}
                alt=""
                aria-hidden="true"
              />
              <span className="screen-reader-text">{t("Search")}</span>
            </button>
            <div
              className={`${styles.layer} ${
                loginOpen ? styles.stateActive : ""
              }`}
              aria-expanded={loginOpen}
            >
              <p>{t("Login to our services")}</p>
              {iconGrid && <IconGrid>{iconGrid}</IconGrid>}
            </div>
          </li>
          <li className={styles.searchTriggerContainer}>
            <button
              className={styles.searchTrigger}
              onClick={() => setSearchOpen(true)}
            >
              <img
                src={iconSearch}
                className={styles.extrasIcon}
                alt=""
                aria-hidden="true"
              />
              <span className="screen-reader-text">{t("Search")}</span>
            </button>
          </li>
        </ul>

        <nav>
          <Menu open={open} menuItems={menuItems} siteSettings={siteSettings} />
        </nav>
      </div>
    </header>
  )
}

Header.propTypes = {
  siteTitle: PropTypes.string,
}

Header.defaultProps = {
  siteTitle: ``,
  currentLanguage: `fi`,
  menuItems: [],
  customClass: ``,
}

export default Header
